/**
 * PreviewLite - 결제 전 미리보기 컴포넌트
 *
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 🔥 P0 조건:
 * - 서버 API 호출 없음 (프론트 데이터만 사용)
 * - 사주 프로필 요약 + Decision 티저만 노출
 * - Money / Action / Calendar / PDF 절대 노출 금지
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

'use client';

import { useMemo } from 'react';
import type { CalculateResponse, Pillar } from '@/types';
import { buildPreviewHeadline } from '@/lib/previewHeadline';
import { getSectionPreview } from '@/lib/previewBuilder';

interface PreviewLiteProps {
  calcResult: CalculateResponse;
  name: string;
  targetYear?: number;
}

// 오행 색상 매핑
const ELEMENT_COLORS: Record<string, string> = {
  '목': 'bg-green-100 text-green-800 border-green-300',
  '화': 'bg-red-100 text-red-800 border-red-300',
  '토': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  '금': 'bg-gray-100 text-gray-800 border-gray-300',
  '수': 'bg-blue-100 text-blue-800 border-blue-300',
  'wood': 'bg-green-100 text-green-800 border-green-300',
  'fire': 'bg-red-100 text-red-800 border-red-300',
  'earth': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'metal': 'bg-gray-100 text-gray-800 border-gray-300',
  'water': 'bg-blue-100 text-blue-800 border-blue-300',
};

// 오행 한글 매핑
const ELEMENT_KO: Record<string, string> = {
  'wood': '목',
  'fire': '화',
  'earth': '토',
  'metal': '금',
  'water': '수',
};

function getElementColor(element: string): string {
  const key = element?.toLowerCase() || '';
  return ELEMENT_COLORS[key] || ELEMENT_COLORS[ELEMENT_KO[key]] || 'bg-slate-100 text-slate-800';
}

function getElementKo(element: string): string {
  const key = element?.toLowerCase() || '';
  return ELEMENT_KO[key] || element || '';
}

// 주(柱) 카드 컴포넌트
function PillarCard({ label, pillar }: { label: string; pillar: Pillar | null }) {
  if (!pillar) {
    return (
      <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="text-xs text-slate-500 mb-1">{label}</div>
        <div className="text-lg text-slate-300">-</div>
        <div className="text-sm text-slate-300">-</div>
      </div>
    );
  }

  return (
    <div className="text-center p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={`text-xl font-bold ${getElementColor(pillar.gan_element)} px-2 py-1 rounded mb-1`}>
        {pillar.gan}
      </div>
      <div className={`text-lg ${getElementColor(pillar.ji_element)} px-2 py-0.5 rounded`}>
        {pillar.ji}
      </div>
      <div className="text-xs text-slate-400 mt-1">
        {getElementKo(pillar.gan_element)}/{getElementKo(pillar.ji_element)}
      </div>
    </div>
  );
}

function LockedTeaserCard({
  title,
  subtitle,
  lines,
}: {
  title: string;
  subtitle: string;
  lines: { tag: string; text: string }[];
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-slate-800">{title}</h4>
          <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="text-xl">🔒</div>
      </div>

      <div className="mt-4 space-y-2">
        {lines.map((l, idx) => (
          <div key={`${l.tag}-${idx}`} className="text-sm text-slate-700 leading-relaxed">
            <span className="inline-block mr-2 text-xs font-semibold text-indigo-700">
              [{l.tag}]
            </span>
            <span>{l.text}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 text-xs text-slate-500">
        ※ 위 내용은 미리보기 티저이며, 구체 전략/근거는 결제 후 공개됩니다.
      </div>
    </div>
  );
}

// 오행 분포 계산
function calculateElementDistribution(saju: CalculateResponse['saju']): Record<string, number> {
  const dist: Record<string, number> = { 목: 0, 화: 0, 토: 0, 금: 0, 수: 0 };

  const pillars = [saju.year_pillar, saju.month_pillar, saju.day_pillar, saju.hour_pillar];

  for (const p of pillars) {
    if (!p) continue;
    const ganEl = getElementKo(p.gan_element);
    const jiEl = getElementKo(p.ji_element);
    if (ganEl && dist[ganEl] !== undefined) dist[ganEl]++;
    if (jiEl && dist[jiEl] !== undefined) dist[jiEl]++;
  }

  return dist;
}

export default function PreviewLite({ calcResult, name, targetYear = 2026 }: PreviewLiteProps) {
  const { saju, day_master, day_master_element, day_master_description } = calcResult;
  const elementDist = calculateElementDistribution(saju);
  const headline = useMemo(
    () => buildPreviewHeadline(calcResult, targetYear, name),
    [calcResult, targetYear, name]
  );
  const moneyPreview = useMemo(() => getSectionPreview(calcResult, 'money'), [calcResult]);
  const businessPreview = useMemo(() => getSectionPreview(calcResult, 'business'), [calcResult]);
  const teamPreview = useMemo(() => getSectionPreview(calcResult, 'team'), [calcResult]);

  return (
    <div className="space-y-6 pb-8">
      {/* 헤더 */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-800">
          🔮 {name}님의 사주 프로필
        </h2>
        <p className="text-slate-500 mt-1">{targetYear}년 비즈니스 운세 미리보기</p>
      </div>

      {/* 맞춤형 한 줄 결론 카드 */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-6">
        <p className="text-xs font-semibold text-indigo-700">맞춤형 한 줄 결론</p>
        <h2 className="mt-1 text-xl md:text-2xl font-extrabold text-slate-900 leading-snug">
          {headline.title}
        </h2>
        <p className="mt-2 text-sm md:text-base text-slate-600 leading-relaxed">
          {headline.subtitle}
        </p>

        {headline.chips.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {headline.chips.map((c) => (
              <span
                key={c}
                className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200"
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 사주 4주 표시 */}
      <div className="bg-gradient-to-br from-purple-50 to-amber-50 rounded-xl p-6 border border-purple-100">
        <h3 className="text-lg font-semibold text-slate-700 mb-4 text-center">
          📊 사주 원국 (四柱原局)
        </h3>

        <div className="grid grid-cols-4 gap-3">
          <PillarCard label="시주" pillar={saju.hour_pillar} />
          <PillarCard label="일주" pillar={saju.day_pillar} />
          <PillarCard label="월주" pillar={saju.month_pillar} />
          <PillarCard label="년주" pillar={saju.year_pillar} />
                  </div>

        {/* 일간 정보 */}
        <div className="mt-6 p-4 bg-white/80 rounded-lg border border-purple-100">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm text-slate-500">일간 (Day Master)</span>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-2xl font-bold px-3 py-1 rounded ${getElementColor(day_master_element)}`}>
                  {day_master}
                </span>
                <span className="text-slate-600">
                  {getElementKo(day_master_element)}
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-sm text-slate-500">성향</span>
              <p className="text-sm text-slate-700 mt-1 max-w-[200px]">
                {day_master_description || '분석 중...'}
              </p>
            </div>
          </div>
        </div>

        {/* 오행 분포 */}
        <div className="mt-4">
          <div className="text-sm text-slate-500 mb-2 text-center">오행 분포</div>
          <div className="flex justify-center gap-2">
            {Object.entries(elementDist).map(([el, count]) => (
              <div
                key={el}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  count > 0 ? getElementColor(el) : 'bg-slate-100 text-slate-400'
                }`}
              >
                {el} {count}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Decision 티저 카드 (블러 처리) */}
      <div className="relative">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
          <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
            🎯 {targetYear}년 핵심 결정 포인트
          </h3>

          {/* 블러 처리된 콘텐츠 */}
          <div className="relative">
            <div className="blur-sm select-none pointer-events-none space-y-3">
              <div className="bg-white/60 rounded-lg p-4">
                <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-slate-100 rounded w-full mb-1"></div>
                <div className="h-3 bg-slate-100 rounded w-5/6"></div>
              </div>
              <div className="bg-white/60 rounded-lg p-4">
                <div className="h-4 bg-slate-200 rounded w-2/3 mb-2"></div>
                <div className="h-3 bg-slate-100 rounded w-full mb-1"></div>
                <div className="h-3 bg-slate-100 rounded w-4/5"></div>
              </div>
              <div className="bg-white/60 rounded-lg p-4">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-slate-100 rounded w-full mb-1"></div>
                <div className="h-3 bg-slate-100 rounded w-3/4"></div>
              </div>
            </div>

            {/* 잠금 오버레이 */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white/90 backdrop-blur-sm rounded-xl px-6 py-4 shadow-lg border border-amber-200 text-center">
                <div className="text-3xl mb-2">🔒</div>
                <p className="text-slate-700 font-medium">결제 후 확인 가능</p>
                <p className="text-sm text-slate-500 mt-1">
                  {targetYear}년 맞춤 전략이 준비되어 있습니다
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 잠금 섹션 미리보기 (GPT 없이 3~6줄) */}
      <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-700 mb-1">🔒 잠금 섹션 미리보기</h3>
        <p className="text-sm text-slate-500 mb-5">
          결제 전에는 "핵심만" 맛보기로 공개됩니다. 상세 근거/전략은 결제 후 잠금 해제됩니다.
        </p>

        <div className="grid md:grid-cols-3 gap-4">
          <LockedTeaserCard
            title="💰 Money — 현금흐름/리스크"
            subtitle="돈이 남는 구조를 만드는 방향성"
            lines={moneyPreview}
          />
          <LockedTeaserCard
            title="🏢 Business — 성장 전략"
            subtitle="이기는 조합을 고정하는 방향성"
            lines={businessPreview}
          />
          <LockedTeaserCard
            title="🤝 Team — 파트너/조직"
            subtitle="충돌을 줄이고 속도를 살리는 방향성"
            lines={teamPreview}
          />
        </div>
      </div>

      {/* 포함 내용 안내 (노출 최소화) */}
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <h4 className="font-medium text-slate-700 mb-2">📋 결제 후 잠금 해제</h4>
        <p className="text-sm text-slate-600">
          지금 보이는 분석을 기반으로, 전체 리포트에서 더 깊은 전략/근거/실행 설계가 이어집니다.
        </p>
      </div>
    </div>
  );
}
